import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Typography, Box, FormControlLabel, Switch, ButtonBase } from "@material-ui/core";
import { COLORS, LOCATION_LEVEL_ICONS } from "../constants";
import { Btn, CollapsibleSection } from "../components/UI";
import { LocationTreeSidebar } from "../components/LocationTreeSidebar";
import { ConfigCard } from "../components/ConfigCard";
import { useStyles } from "./browse-view.style";

const DIRECT_ONLY_STORAGE_KEY = "location-config-manager.direct-only";
const PROGRAMS_ONLY_STORAGE_KEY = "location-config-manager.programs-only";

const readStoredToggleValue = (storageKey) => {
  try {
    return window.localStorage.getItem(storageKey) === "true";
  } catch {
    return false;
  }
};

export function BrowseView({
  selectedLocation,
  setSelectedLocation,
  expandedNodes,
  toggleExpanded,
  config,
  locationNode,
  locationChain,
  openTemplateFromLocation,
  setTab,
  editId,
  editType,
  editData,
  setEditData,
  deleteConfirmation,
  setDeleteConfirmation,
  startEdit,
  cancelEdit,
  saveEdit,
  doDelete,
  locations,
  configLoading,
  configError,
  servicesRef,
  resourcesRef,
}) {
  const classes = useStyles();
  const [servicesExpanded, setServicesExpanded] = useState(true);
  const [resourcesExpanded, setResourcesExpanded] = useState(true);
  const [directOnly, setDirectOnly] = useState(() => readStoredToggleValue(DIRECT_ONLY_STORAGE_KEY));
  const [programsOnly, setProgramsOnly] = useState(() => readStoredToggleValue(PROGRAMS_ONLY_STORAGE_KEY));

  useEffect(() => {
    try {
      window.localStorage.setItem(DIRECT_ONLY_STORAGE_KEY, String(directOnly));
    } catch {
      // Ignore storage errors (private mode, blocked storage, etc.)
    }
  }, [directOnly]);

  useEffect(() => {
    try {
      window.localStorage.setItem(PROGRAMS_ONLY_STORAGE_KEY, String(programsOnly));
    } catch {
      // Ignore storage errors (private mode, blocked storage, etc.)
    }
  }, [programsOnly]);

  const onSelectLocation = useCallback(
    (id) => {
      setSelectedLocation(id);
      cancelEdit();
      setDeleteConfirmation(null);
    },
    [setSelectedLocation, cancelEdit, setDeleteConfirmation]
  );

  const onGoToInheritedLocation = (fromLoc) => {
    setSelectedLocation(fromLoc);
    cancelEdit();
    setTab("browse");
  };

  const rawServices = config?.services ?? [];
  const rawResources = config?.resources ?? [];
  const servicesList = directOnly
    ? rawServices.filter((service) => service.origin !== "inherited")
    : rawServices;
  const resourcesFilteredByDirect = directOnly
    ? rawResources.filter((resource) => resource.origin !== "inherited")
    : rawResources;
  const resourcesList = programsOnly
    ? resourcesFilteredByDirect.filter((resource) => resource.parentResourceId == null)
    : resourcesFilteredByDirect;

  return (
    <Box className={classes.root}>
      <LocationTreeSidebar
        title="Location Hierarchy"
        locations={locations}
        mode="browse"
        selected={selectedLocation}
        onSelect={onSelectLocation}
        expanded={expandedNodes}
        onToggle={toggleExpanded}
      />
      <Box className={classes.mainContent} aria-busy={configLoading}>
        <Box className={classes.breadcrumbs}>
          {locationChain.map((node, index) => (
            <Typography component="span" key={node.id} variant="caption">
              {index > 0 && <Typography component="span" variant="caption" sx={{ margin: "0 3px" }}>›</Typography>}
              <ButtonBase
                onClick={() => setSelectedLocation(node.id)}
                className={`${classes.breadcrumbButton} ${index === locationChain.length - 1 ? classes.breadcrumbActive : classes.breadcrumbInactive}`}
              >
                {node.name}
              </ButtonBase>
            </Typography>
          ))}
        </Box>
        {(configLoading || configError) && (
          <Typography
            className={classes.statusMessage}
            sx={{ color: configError ? COLORS.error : COLORS.muted }}
            component="div"
            role="status"
            aria-live="polite"
          >
            {configLoading && "Loading config…"}
            {!configLoading && configError && `Error: ${configError}`}
          </Typography>
        )}
        <Box className={classes.filterBar} role="group" aria-label="Filter config list">
          <FormControlLabel
            control={
              <Switch
                checked={directOnly}
                onChange={(event) => setDirectOnly(event.target.checked)}
                size="small"
                color="primary"
                inputProps={{ "aria-label": "Show only config set at this location" }}
              />
            }
            label="Direct only"
          />
          <FormControlLabel
            control={
              <Switch
                checked={programsOnly}
                onChange={(event) => setProgramsOnly(event.target.checked)}
                size="small"
                color="primary"
                inputProps={{ "aria-label": "Show only program-level resources" }}
              />
            }
            label="Programs only"
          />
        </Box>
        <Box className={classes.locationHeader}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: COLORS.body }}>
            {LOCATION_LEVEL_ICONS[locationNode?.level ?? 0]} {locationNode?.name}
          </Typography>
          <Box className={classes.locationActions}>
            <Btn variant="outlined" size="small" onClick={() => openTemplateFromLocation(selectedLocation)}>
              Template from Location
            </Btn>
          </Box>
        </Box>
        <CollapsibleSection
          title={`Services (${servicesList.length})`}
          expanded={servicesExpanded}
          onToggle={() => setServicesExpanded((prev) => !prev)}
        >
          {servicesList.length === 0 && (
            <Typography className={classes.emptyState}>
              No services at this location.
            </Typography>
          )}
          {servicesList.map((service) => (
            <ConfigCard
              key={`svc-${service.id}`}
              item={service}
              type="svc"
              isEditing={editId === service.id && editType === "svc"}
              editData={editData}
              deleteConfirmation={deleteConfirmation}
              onStartEdit={startEdit}
              onCancelEdit={cancelEdit}
              onEditDataChange={setEditData}
              onSaveEdit={saveEdit}
              onDeleteClick={(id) => setDeleteConfirmation({ type: "svc", id })}
              onDeleteConfirm={doDelete}
              onClearDeleteConfirm={() => setDeleteConfirmation(null)}
              onGoToInheritedLocation={onGoToInheritedLocation}
              servicesRef={servicesRef}
              resourcesRef={resourcesRef}
            />
          ))}
        </CollapsibleSection>
        <CollapsibleSection
          title={`Resources (${resourcesList.length})`}
          expanded={resourcesExpanded}
          onToggle={() => setResourcesExpanded((prev) => !prev)}
        >
          {resourcesList.length === 0 && (
            <Typography className={classes.emptyState}>
              No resources at this location.
            </Typography>
          )}
          {resourcesList.map((resource) => (
            <ConfigCard
              key={`res-${resource.id}`}
              item={resource}
              type="res"
              isEditing={editId === resource.id && editType === "res"}
              editData={editData}
              deleteConfirmation={deleteConfirmation}
              onStartEdit={startEdit}
              onCancelEdit={cancelEdit}
              onEditDataChange={setEditData}
              onSaveEdit={saveEdit}
              onDeleteClick={(id) => setDeleteConfirmation({ type: "res", id })}
              onDeleteConfirm={doDelete}
              onClearDeleteConfirm={() => setDeleteConfirmation(null)}
              onGoToInheritedLocation={onGoToInheritedLocation}
              servicesRef={servicesRef}
              resourcesRef={resourcesRef}
            />
          ))}
        </CollapsibleSection>
      </Box>
    </Box>
  );
}

BrowseView.propTypes = {
  selectedLocation: PropTypes.string,
  setSelectedLocation: PropTypes.func.isRequired,
  expandedNodes: PropTypes.instanceOf(Set),
  toggleExpanded: PropTypes.func.isRequired,
  config: PropTypes.shape({ services: PropTypes.array, resources: PropTypes.array }),
  locationNode: PropTypes.shape({ level: PropTypes.number, name: PropTypes.string }),
  locationChain: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, name: PropTypes.string })),
  openTemplateFromLocation: PropTypes.func.isRequired,
  setTab: PropTypes.func.isRequired,
  editId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  editType: PropTypes.string,
  editData: PropTypes.object,
  setEditData: PropTypes.func.isRequired,
  deleteConfirmation: PropTypes.object,
  setDeleteConfirmation: PropTypes.func.isRequired,
  startEdit: PropTypes.func.isRequired,
  cancelEdit: PropTypes.func.isRequired,
  saveEdit: PropTypes.func.isRequired,
  doDelete: PropTypes.func.isRequired,
  locations: PropTypes.array,
  configLoading: PropTypes.bool,
  configError: PropTypes.string,
  servicesRef: PropTypes.array,
  resourcesRef: PropTypes.array,
};

BrowseView.defaultProps = {
  selectedLocation: undefined,
  expandedNodes: undefined,
  config: undefined,
  locationNode: undefined,
  locationChain: [],
  editId: undefined,
  editType: undefined,
  editData: null,
  deleteConfirmation: null,
  locations: [],
  configLoading: false,
  configError: null,
  servicesRef: [],
  resourcesRef: [],
};
